import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {Link} from 'shells/link/link';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {Body} from 'shells/typography/body';
import {DescriptionBody} from 'shells/typography/description-body';

import styles from './report-empty-state-card.module.css';

type Props = {
    reportKey: string;
};

const emptyStates = {
    sales: {
        label: 'No sales in this period',
        imagePath: '/include/images/report/sales-report.png',
        description: (
            <DescriptionBody>
                Review won leads in the <b>sales report</b> and celebrate all the deals your team
                closes
            </DescriptionBody>
        ),
        trial: {
            description: (
                <DescriptionBody>
                    Add lead data to unlock this report and track your team’s sales performance
                </DescriptionBody>
            ),
            cta: {
                label: 'Import leads',
                to: '/setup/import',
            },
        },
    },
    losses: {
        label: 'No losses in this period',
        imagePath: '/include/images/report/losses-report.png',
        description: (
            <DescriptionBody>
                Identify trends with the <b>losses report</b> that contribute to lead loss and
                negatively impact your sales cycle
            </DescriptionBody>
        ),
        trial: {
            description: (
                <DescriptionBody>
                    Add lead data to unlock this report and identify trends that contribute to lead
                    loss and negatively impact your sales cycle
                </DescriptionBody>
            ),
            cta: {
                label: 'Import leads',
                to: '/setup/import',
            },
        },
    },
    newleads: {
        label: 'No new leads in this period',
        imagePath: '/include/images/report/new-leads-report.png',
        description: (
            <DescriptionBody>
                Check out the <b>new leads report</b> to see how your prospecting efforts pay off
            </DescriptionBody>
        ),
        trial: {
            description: (
                <DescriptionBody>
                    Add lead data to unlock this report and see how your prospecting efforts pay off
                </DescriptionBody>
            ),
            cta: {
                label: 'Import leads',
                to: '/setup/import',
            },
        },
    },
    forecast: {
        label: 'There’s not enough data to forecast for this period',
        imagePath: '/include/images/report/forecast-report.png',
        description: (
            <DescriptionBody>
                Estimate future revenue with the <b>forecast report</b> by weighing the confidence
                and value of your open leads
            </DescriptionBody>
        ),
        trial: {
            description: (
                <DescriptionBody>
                    Add lead data to unlock this report and estimate future revenue by weighing the
                    confidence and value of your open leads
                </DescriptionBody>
            ),
            cta: {
                label: 'Import leads',
                to: '/setup/import',
            },
        },
    },
    siteAnalytics: {
        label: 'No website traffic in this period',
        imagePath: '/include/images/report/analytics-report.png',
        description: (
            <DescriptionBody>
                Learn about website traffic with the <b>analytics report</b> to see your total page
                views and unique site visitors
            </DescriptionBody>
        ),
    },
    activity: {
        label: 'No activity data in this period',
        imagePath: '/include/images/report/custom-report.png',
        description: (
            <DescriptionBody>
                Track your team‘s productivity with the <b>activity report</b> and see how they
                perform against your quotas
            </DescriptionBody>
        ),
        trial: {
            description: (
                <DescriptionBody>
                    Create an activity to unlock this report and see how your team performs against
                    your quotas
                </DescriptionBody>
            ),
            cta: {
                label: 'Create an activity',
                to: {
                    hash: '/create/activities',
                    search: window.location.search,
                    state: {modal: true},
                },
            },
        },
    },
    email: {
        label: 'No emails sent in this period',
        imagePath: '/include/images/report/custom-report.png',
        description: (
            <DescriptionBody>
                Learn about your team’s email activity with the <b>email report</b>
            </DescriptionBody>
        ),
        trial: {
            description: (
                <DescriptionBody>
                    Send an email to a contact or lead to unlock this report and learn about your
                    team’s email activity
                </DescriptionBody>
            ),
            cta: {
                label: 'Connect your email',
                to: '/my-account/email-sending',
            },
        },
    },
    tasks: {
        label: 'No task data found in this period',
        imagePath: '/include/images/report/custom-report.png',
        description: (
            <DescriptionBody>
                Track team performance with the <b>task report</b>
            </DescriptionBody>
        ),
        trial: {
            description: (
                <DescriptionBody>
                    Create automated tasks in your pipelines to unlock this report and track team
                    performance
                </DescriptionBody>
            ),
            cta: {
                label: 'View pipelines',
                to: '/sales/pipelines',
            },
        },
    },
    custom: {
        label: 'Nothing to see here… yet!',
        imagePath: '/include/images/report/custom-report.png',
        description: (
            <DescriptionBody>
                Get the lead data you need with <b>custom reports</b> that measure your team’s
                unique sales process
            </DescriptionBody>
        ),
    },
} as const;

export function ReportEmptyStateCard(props: Props) {
    // Default to the custom report for safety
    const {label, imagePath, description, trial} =
        // @ts-expect-error
        emptyStates[props.reportKey] || emptyStates.custom;

    const isTrial = NutClientConfig.isTrial;
    const descriptionToRender = isTrial && trial ? trial.description : description;

    return (
        <div className={styles['empty-state']}>
            <div className='pos-absolute top-0 left-0 full-width full-height flex flex-column justify-center align-center'>
                <div className={styles['info-card']}>
                    <SpacerGroup mb={16}>{!isTrial && <Body>{label}</Body>}</SpacerGroup>
                    <SpacerGroup mb={16}>
                        <img src={imagePath} alt='report graphic' />
                    </SpacerGroup>
                    <div className='flex flex-dir-col gap-16 justify-center align-center'>
                        <div className={styles.description}>{descriptionToRender}</div>
                        {isTrial && trial && (
                            <Link
                                // @ts-expect-error
                                as={RoutedLink}
                                variant='button-primary'
                                size='big'
                                to={trial.cta.to}
                            >
                                {trial.cta.label}
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
